import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

const links = [
  { href: "#about", label: "About" },
  { href: "#services", label: "Services" },
  { href: "#results", label: "Impact" },
  { href: "#industries", label: "Industries" },
  { href: "#testimonials", label: "Testimonials" },
  { href: "#contact", label: "Contact" },
];

export const Navbar = ({ onBookClick }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-[#051024]/90 backdrop-blur-md border-b border-[#233554]" : "bg-transparent"
      }`}
      data-testid="site-navbar"
    >
      <div className="px-6 md:px-12 lg:px-24 py-5 flex items-center justify-between">
        <a href="#hero" className="flex items-center gap-3 group" data-testid="nav-logo">
          <div className="w-10 h-10 border border-[#D4AF37] flex items-center justify-center">
            <span className="font-serif text-[#D4AF37] text-lg">PD</span>
          </div>
          <div className="hidden sm:block leading-tight">
            <p className="font-serif text-[#F8F9FA] text-sm tracking-wide">Project DeliverEdge</p>
            <p className="text-[10px] uppercase tracking-[0.25em] text-[#A8B2D1]">Consulting</p>
          </div>
        </a>

        <nav className="hidden lg:flex items-center gap-9">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              className="text-sm text-[#A8B2D1] hover:text-[#D4AF37] tracking-wide transition-colors duration-300"
              data-testid={`nav-link-${l.label.toLowerCase()}`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={onBookClick}
            className="hidden md:inline-flex items-center gap-2 bg-[#D4AF37] text-[#051024] hover:bg-[#F1D570] transition-colors duration-300 px-6 py-3 text-sm font-semibold tracking-wide"
            data-testid="nav-book-consultation-btn"
          >
            Book a Consultation
          </button>
          <button
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden text-[#F8F9FA] p-2"
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="lg:hidden bg-[#051024]/95 border-t border-[#233554] overflow-hidden"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-4 flex flex-col gap-3">
              {links.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className="text-[#A8B2D1] hover:text-[#D4AF37] py-2 text-sm tracking-wide"
                  data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                >
                  {l.label}
                </a>
              ))}
              <button
                onClick={() => { setOpen(false); onBookClick(); }}
                className="mt-2 bg-[#D4AF37] text-[#051024] px-5 py-3 text-sm font-semibold"
                data-testid="mobile-book-consultation-btn"
              >
                Book a Consultation
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
